/**
 * Created by mariushirjanu on 04/10/2019.
 */
import _ from 'underscore';
import { Editor, RawEditorOptions } from 'tinymce';
import { AuthService } from '../services/auth/auth.service';
import { UrlService } from '../browser/url.service';
import { Inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { IExtendedWindow } from '@type_definitions/datatypes';
import { IUploadFile, IUploadFileCompleteEvent, IUploadFormData } from '../multi-file-upload/multi-file-upload.component';
import { TranslationService } from '../translation/translation.service';
import { DialogSize, GaMessageService, IDialog } from '../message/ga-message.service';
import { UploadFilesDialogComponent } from './upload-files-dialog.component';
import { HttpResponseStatus, IHttpDataMessage } from '../http/http-data.service';
import { DocumentTypeCode, DocumentUploadSource, TEMP_DOCUMENTS_UPLOAD_URL } from '@app/shared/document/document-data.service';

export type TextEditor = 'tinymce';
export type EditorCode = 'tinymce_simple' | 'tinymce_advanced';
type EditorImageUploadHandler = (msg:string) => void;

export const EDITOR_TINYMCE:TextEditor = 'tinymce';
export const TINYMCE_ADVANCED:EditorCode = 'tinymce_advanced';
export const TINYMCE_SIMPLE:EditorCode = 'tinymce_simple';
const UPLOAD_TYPE_IMAGE:string = 'upload_image';
const UPLOAD_TYPE_ATTACHMENT:string = 'upload_attachment';

@Injectable()
export class EditorConfigService {
  private uploadEventSubject: Subject<number[]> = new Subject<number[]>();
  public onUploadCompleted: Observable<number[]> = this.uploadEventSubject.asObservable();

  constructor(
    @Inject(Window) private window:IExtendedWindow,
    private messageService:GaMessageService,
    private translationService:TranslationService,
    private urlService:UrlService,
    private authService:AuthService
  ) {
    /**/
  }

  private tinymceSimple():RawEditorOptions {
    return {
      menubar: false,
      toolbar: 'bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | forecolor backcolor | copy paste pastetext | bullist | undo redo | link unlink',
      plugins: 'paste link lists',
      min_height: 200,
      content_style: this.editorContentStyles,
      browser_spellcheck: true,
      relative_urls: false,
      entity_encoding: 'raw' // do not replace diacritics with html entities (required for db search)
    };
  }

  private tinymceAdvanced():RawEditorOptions {
    return <any>{
      menubar: false,
      debounce: false,
      toolbar: 'bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | forecolor backcolor | copy paste pastetext | bullist | undo redo | link unlink | insertImage insertAttachment',
      plugins: 'paste link lists image',
      min_height: 320,
      paste_data_images: true,
      content_style: this.editorContentStyles,
      setup: (editor:Editor) => this.tinymceAdvancedSetup(editor),
      images_upload_handler: (blobInfo:any, success:EditorImageUploadHandler, failure:EditorImageUploadHandler) => this.dragOrPasteImageToEditorUploadHandler(blobInfo, success, failure),
      browser_spellcheck: true,
      relative_urls: false,
      entity_encoding: 'raw' // do not replace diacritics with html entities (required for db search)
    };
  }

  public getConfig(editorCode?:EditorCode) {
    editorCode = editorCode || TINYMCE_SIMPLE; // TINYMCE_SIMPLE is the default
    return editorCode === TINYMCE_ADVANCED? this.tinymceAdvanced() : this.tinymceSimple();
  }

  // parses the editor's content and extract the IDs of all documents (images & attachments)
  public extractDocumentIdsFromContent(content:string):number[] {
    const contentDocumentIds:number[] = [];
    // use this approach until matchAll is implemented in ECMAScript 2020: https://tc39.es/ecma262/#sec-string.prototype.matchall
    const ATTACHMENT_URL_REGEXP:RegExp = / data-file-id="([0-9]+)"/ig;
    let match:RegExpMatchArray;
    while ((match = ATTACHMENT_URL_REGEXP.exec(content)) !== null) {
      if (match[ 1 ]) {
        contentDocumentIds.push(parseInt(match[ 1 ]));
      }
    }
    return contentDocumentIds;
  }

  // extract the IDs of all uploaded documents (images & attachments)
  private extractDocumentIdsFromUpload = (documents:IUploadFile | IUploadFile[]):number[] => {
    const uploadedDocumentIds:number[] = [];
    (<IUploadFile[]>documents) = _.isArray(documents)? <IUploadFile[]>documents : [ <IUploadFile>documents ];
    _.each(<IUploadFile[]>documents, (file:IUploadFile) => {
      if (file.response.data && file.response.data.id) {
        uploadedDocumentIds.push(file.response.data.id);
      }
    });
    return _.uniq(uploadedDocumentIds);
  };


  // ---------------------------------------------------------------------------------------------------------


  private tinymceAdvancedSetup = (editor:Editor):void => {
    const imageTitle:string = this.translationService.instant('APP.COMPONENTS.MULTIFILEUPLOAD.INSERT_IMAGE');
    const attachmentTitle:string = this.translationService.instant('APP.COMPONENTS.MULTIFILEUPLOAD.INSERT_ATTACHMENT');

    (<any>editor).ui.registry.addButton('insertImage', {
      icon: 'image',
      tooltip: imageTitle,
      onAction: () => {
        this.showUploadFilesDialog(editor, imageTitle, UPLOAD_TYPE_IMAGE);
      }
    });

    (<any>editor).ui.registry.addButton('insertAttachment', {
      icon: 'document-properties',
      tooltip: attachmentTitle,
      onAction: () => {
        this.showUploadFilesDialog(editor, attachmentTitle, UPLOAD_TYPE_ATTACHMENT);
      }
    });
  };

  // this method will be executed at the moment when an image is dragged or pasted directly in the TinyMCE editor
  private dragOrPasteImageToEditorUploadHandler = (blobInfo:any, success:EditorImageUploadHandler, failure:EditorImageUploadHandler):void => {
    const xhr:XMLHttpRequest = new XMLHttpRequest();
    // upload method and url
    xhr.open('POST', this.authService.authenticatedUrl(TEMP_DOCUMENTS_UPLOAD_URL));

    xhr.onload = ():void => {
      try {
        const response:IHttpDataMessage<IUploadFile> = JSON.parse(xhr.responseText);

        if (response.status === HttpResponseStatus.FAIL || xhr.status < 200 || xhr.status >= 300) {
          failure(response?.message || 'Error uploading pasted image');
          return;
        }

        // send event callback to tinymce to update image src attribute
        success(this.urlService.getImageUrl(response.data?.guid));

        setTimeout(() => {
          // trigger a 'blur' event to replace the image src with the uploaded path of the image
          this.window.tinyMCE.activeEditor.fire('blur');
          // notify subscribers
          this.uploadEventSubject.next(this.extractDocumentIdsFromUpload(response?.data));
        }, 100);
      } catch(e) { /**/ }
    };

    const formData:FormData = new FormData();
    formData.append('file', new File([ blobInfo.blob() ], blobInfo.blob().name));
    formData.set('documentUploadSource', DocumentUploadSource.TINYMCE_EDITOR);
    formData.set('type', DocumentTypeCode.PICTURE);
    xhr.send(formData);
  };

  private showUploadFilesDialog = (editor:Editor, title:string, type:string):void => {
    const onDialogCancel = ():void => {
      dialog.hide();
    };

    const onUploadCompleted = (event:IUploadFileCompleteEvent):void => {
      dialog.content.inputs.uploading = false;
      if (event.success) {
        insertContent(event.files);
        onDialogCancel();
        this.uploadEventSubject.next(this.extractDocumentIdsFromUpload(event.files));
      }
    };

    const dialog:IDialog = this.messageService.showDialog({
      component: UploadFilesDialogComponent,
      wrapperComponent: false,
      inputs: {
        title,
        uploading: false,
        postUrl: TEMP_DOCUMENTS_UPLOAD_URL,
        addLabel: type === UPLOAD_TYPE_IMAGE? this.translationService.instant('GA.GENERAL.IMAGES') : this.translationService.instant('GA.GENERAL.ATTACHMENTS'),
        filesAccepted: type === UPLOAD_TYPE_IMAGE ? ['images/*'] : undefined,
        uploadFormData: <IUploadFormData>{
          documentUploadSource: DocumentUploadSource.TINYMCE_EDITOR,
          documentTypeCode: type === UPLOAD_TYPE_IMAGE? DocumentTypeCode.PICTURE : undefined
        }
      },
      outputs: {
        onCancel: onDialogCancel,
        onUploadCompleted
      },
      size: DialogSize.MD
    });

    const insertContent = (files:IUploadFile[]):void => {
      const fileTag:string[] = [];
      _.each(files, (file:IUploadFile) => {
        const fileId:number = file.response?.data?.id;
        const guid:string = file.response?.data?.guid;
        if (type === UPLOAD_TYPE_IMAGE) {
          fileTag.push(`<img src="${this.urlService.getImageUrl(guid)}" title="${file.name}" alt="${file.name}" data-file-id="${fileId}" />`);
        } else if (type === UPLOAD_TYPE_ATTACHMENT) {
          fileTag.push(`<a href="${this.urlService.getAttachmentUrl(fileId)}" title="${file.name}" target="_blank" data-file-id="${fileId}" />${file.name}</a>`);
        }
      });
      editor.insertContent(fileTag.join('<br />'));
    };
  };

  private get editorContentStyles():string {
    return `
      body { font-size: 14px; font-family: "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif; color: #222; }
      img {max-width: 100%};
    `;
  }
}